// ============================================================================
// SLIDER CONFIGURATION & UTILITIES
// ============================================================================

const SLIDER_CONFIG = {
  DRAG_THRESHOLD: 8,
  SCROLL_DURATION: 300,
  MOMENTUM_DECAY: 0.92,
  VELOCITY_THRESHOLD: 0.5,
  VELOCITY_MULTIPLIER: 0.8,
  DRAG_MULTIPLIER: 1.5,
  MOMENTUM_MIN_VELOCITY: 2,
  MOBILE_BREAKPOINT: 749
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const isMobile = () => window.innerWidth <= SLIDER_CONFIG.MOBILE_BREAKPOINT;

// ============================================================================
// SLIDER CALCULATIONS
// ============================================================================

export class SliderCalculator {
  constructor(slider) {
    this.slider = slider;
    this.track = slider.querySelector('.slider-track');
    this.update();
  }

  update() {
    if (!this.track) return;
    const children = Array.from(this.track.children);

    if (!children.length) {
      this.columnWidth = 0;
      this.gap = 0;
      this.snapPoints = [];
      return;
    }

    const firstChild = children[0];
    const firstRect = firstChild.getBoundingClientRect();
    this.columnWidth = firstRect.width;

    const gapFromCSS = getComputedStyle(this.track).getPropertyValue('gap').trim() || getComputedStyle(this.slider).getPropertyValue('--gap').trim();

    if (gapFromCSS) {
      this.gap = parseFloat(gapFromCSS);
      if (gapFromCSS.includes('rem')) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        this.gap = this.gap * rootFontSize;
      } else if (gapFromCSS.includes('em')) {
        const fontSize = parseFloat(getComputedStyle(this.slider).fontSize);
        this.gap = this.gap * fontSize;
      }
    } else {
      if (children[1]) {
        const secondRect = children[1].getBoundingClientRect();
        this.gap = secondRect.left - firstRect.left - this.columnWidth;
      } else {
        this.gap = 0;
      }
    }

    this.slidesPerView = this.getSlidesPerView();
    this.slidesPerScroll = this.getSlidesPerScroll();

    this.snapPoints = this.calculateSnapPoints();
  }

  getSlidesPerView() {
    const mobile = isMobile();
    const mobileAttr = this.slider.getAttribute('data-mobile-slides-to-show');
    const desktopAttr = this.slider.getAttribute('data-slides-to-show');

    if (mobile && mobileAttr) return parseInt(mobileAttr, 10);
    return desktopAttr ? parseInt(desktopAttr, 10) : 1;
  }

  getSlidesPerScroll() {
    const mobile = isMobile();
    const mobileAttr = this.slider.getAttribute('data-mobile-slides-to-scroll');
    const desktopAttr = this.slider.getAttribute('data-slides-to-scroll');

    if (mobile && mobileAttr) return parseInt(mobileAttr, 10);
    if (desktopAttr) return parseInt(desktopAttr, 10);
    return this.slidesPerView;
  }

  calculateSnapPoints() {
    if (!this.track) return [];
    const children = Array.from(this.track.children);
    if (!children.length) return [];

    const sliderWidth = this.slider.getBoundingClientRect().width;
    const trackWidth = children.reduce((width, slide) => width + slide.getBoundingClientRect().width, 0) + (children.length - 1) * this.gap;
    const maxScroll = Math.max(0, trackWidth - sliderWidth);
    const snapPoints = [];

    for (let i = 0; i < children.length; i += this.slidesPerScroll) {
      const position = i * (this.columnWidth + this.gap);
      if (position <= maxScroll) {
        snapPoints.push(position);
      }
    }

    if (snapPoints[snapPoints.length - 1] < maxScroll) {
      snapPoints.push(maxScroll);
    }

    return [...new Set(snapPoints)];
  }

  findNearestSnapPoint(currentPosition = 0) {
    if (!this.snapPoints.length) return 0;
    return this.snapPoints.reduce((nearest, snap) =>
      Math.abs(currentPosition - snap) < Math.abs(currentPosition - nearest) ? snap : nearest
      , this.snapPoints[0]);
  }

  getNextSnapPoint(currentPosition = 0) {
    const nextSnap = this.snapPoints.find(snap => snap > currentPosition + 1);
    return nextSnap !== undefined ? nextSnap : this.snapPoints[this.snapPoints.length - 1] || 0;
  }

  getPrevSnapPoint(currentPosition = 0) {
    const reversedSnaps = [...this.snapPoints].reverse();
    const prevSnap = reversedSnaps.find(snap => snap < currentPosition - 1);
    return prevSnap !== undefined ? prevSnap : 0;
  }
}

// ============================================================================
// DRAGGABLE SLIDER (NOW USES TRANSFORM)
// ============================================================================

export class DraggableSlider {
  constructor(slider) {
    this.slider = slider;
    this.track = slider.querySelector('.slider-track');
    if (!this.track) {
      console.error('Slider requires a child with class .slider-track', slider);
      return;
    }

    this.calculator = new SliderCalculator(slider);
    this.lastWidth = window.innerWidth;
    this.animationType = slider.getAttribute('data-animation-type') || 'slide';
    this.infiniteLoop = slider.getAttribute('data-infinite-loop') === 'true';
    this.originalSlides = []; // Store references to original slides
    this.isJumping = false; // Prevent interactions during instant repositioning

    // Check if slider should be initialized
    if (!this.shouldInitialize()) {
      this.slider.classList.add('slider-disabled');
      return;
    }

    this.slider.sliderInstance = this; // Make instance accessible to other classes

    this.state = {
      isDown: false,
      isDragging: false,
      wasDragging: false,
      startX: 0,
      startY: 0,
      startPosition: 0,
      currentPosition: 0,
      velocity: 0,
      lastX: 0,
      animationId: null,
      isAnimating: false,
      scrollDirection: null // Track if user is scrolling vertically or horizontally
    };

    this.resizeTimeout = null;
    this.isResizing = false;

    this.init();
  }

  shouldInitialize() {
    // Always initialize if infinite loop is enabled
    if (this.infiniteLoop) return true;

    const slideCount = this.track.children.length;
    const slidesToShow = this.calculator.slidesPerView;
    return slideCount > slidesToShow;
  }

  getSlidesToClone() {
    // Calculate max slides to show across breakpoints to ensure enough clones
    const desktopSlides = parseInt(this.slider.getAttribute('data-slides-to-show') || 1, 10);
    const mobileSlides = parseInt(this.slider.getAttribute('data-mobile-slides-to-show') || 1, 10);
    const maxSlidesToShow = Math.max(desktopSlides, mobileSlides);

    // Add +1 buffer to be safe against rounding/gap issues
    return Math.max(maxSlidesToShow, this.calculator.slidesPerScroll) + 1;
  }

  setupInfiniteLoop() {
    if (!this.infiniteLoop) return;

    const slides = Array.from(this.track.children);

    // Check if clones already exist (prevent double initialization)
    const existingClones = slides.filter(slide => slide.classList.contains('slider-clone'));
    if (existingClones.length > 0) {
      console.warn('Slider already has clones, skipping setupInfiniteLoop');
      // Just set up the originalSlides reference without re-cloning
      this.originalSlides = slides.filter(slide => !slide.classList.contains('slider-clone'));

      // Calculate initial position - use same logic as new setup
      const slideWidth = this.calculator.columnWidth + this.calculator.gap;

      const slidesToClone = this.getSlidesToClone();
      const initialOffset = slidesToClone * slideWidth;

      this.state.currentPosition = initialOffset;
      this.track.style.transform = `translateX(${-initialOffset}px)`;
      return;
    }

    this.originalSlides = slides;

    // Clone enough slides to cover the viewport when wrapping
    // We need enough clones to cover the maximum possible view width plus scroll amount
    const slidesToClone = this.getSlidesToClone();

    // Clone slides from the end and prepend
    for (let i = 0; i < slidesToClone; i++) {
      // Use modulo to wrap around if we need more clones than original slides
      const index = (slides.length - 1 - (i % slides.length));
      const clone = slides[index].cloneNode(true);
      clone.classList.add('slider-clone', 'clone-before');
      clone.removeAttribute('id'); // Remove IDs to avoid duplicates
      this.track.insertBefore(clone, this.track.firstChild);
    }

    // Clone slides from the beginning and append
    for (let i = 0; i < slidesToClone; i++) {
      const index = i % slides.length;
      const clone = slides[index].cloneNode(true);
      clone.classList.add('slider-clone', 'clone-after');
      clone.removeAttribute('id');
      this.track.appendChild(clone);
    }

    // Update calculator after cloning
    this.calculator.update();

    // Calculate initial position (skip cloned slides at start)
    const slideWidth = this.calculator.columnWidth + this.calculator.gap;
    const initialOffset = slidesToClone * slideWidth;

    // Set initial position
    this.state.currentPosition = initialOffset;
    this.track.style.transform = `translateX(${-initialOffset}px)`;
  }

  init() {
    // Disable native scrolling behavior for horizontal only
    this.slider.style.userSelect = 'none';
    this.slider.style.webkitUserSelect = 'none';

    // Add animation type class to slider
    this.slider.classList.add(`animation-${this.animationType}`);

    // Setup infinite loop if enabled
    this.setupInfiniteLoop();

    // Initialize fade slider at first slide
    if (this.animationType === 'fade' && !this.infiniteLoop) {
      this.state.currentPosition = 0;
      this.track.style.transform = `translateX(0px)`;
      // Dispatch event so dots and arrows can update
      setTimeout(() => {
        this.slider.dispatchEvent(new CustomEvent('sliderMoved'));
      }, 0);
    }

    this.slider.addEventListener('dragstart', (e) => e.preventDefault());

    // Add drag/touch events for both slide and fade animations
    // Mouse events
    this.slider.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.slider.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.slider.addEventListener('mouseup', () => this.handleMouseUp());
    this.slider.addEventListener('mouseleave', () => this.handleMouseUp());

    // Touch events
    this.slider.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    this.slider.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    this.slider.addEventListener('touchend', () => this.handleTouchEnd());
    this.slider.addEventListener('touchcancel', () => this.handleTouchEnd());

    this.slider.addEventListener('click', (e) => this.handleClick(e), true);
    this.slider.addEventListener('selectstart', (e) => { if (this.state.isDragging) e.preventDefault(); });

    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.slider);
    window.addEventListener('resize', () => this.onResize());
  }

  onResize() {
    const currentWidth = window.innerWidth;
    if (currentWidth === this.lastWidth) return;

    if (!this.isResizing) {
      this.isResizing = true;
      this.slider.dispatchEvent(new CustomEvent('sliderResizeStart'));
    }

    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);

    this.resizeTimeout = setTimeout(() => {
      this.handleResize();
    }, 250);
  }

  handleResize() {
    const currentWidth = window.innerWidth;

    // Calculate current slide index before updating dimensions
    const slideWidth = this.calculator.columnWidth + this.calculator.gap;
    let currentSlideIndex = 0;

    // If previous width was 0 or invalid, we should reset to start
    // This happens when slider is initialized while hidden (e.g. in a tab)
    const wasInvalid = slideWidth <= 0 || isNaN(slideWidth);

    if (!wasInvalid) {
      if (this.infiniteLoop) {
        const slidesToClone = this.getSlidesToClone();
        const originalStartPosition = slidesToClone * slideWidth;
        currentSlideIndex = Math.round((this.state.currentPosition - originalStartPosition) / slideWidth);
      } else {
        currentSlideIndex = Math.round(this.state.currentPosition / slideWidth);
      }
    }

    // Update calculator with new dimensions
    this.calculator.update();

    // Check if slider should be active
    if (!this.shouldInitialize()) {
      this.slider.classList.add('slider-disabled');
      if (this.state.currentPosition !== 0) {
        this.instantJumpTo(0);
      }
      this.lastWidth = currentWidth;
      this.isResizing = false;
      this.slider.dispatchEvent(new CustomEvent('sliderRecalculated'));
      return;
    }

    this.slider.classList.remove('slider-disabled');

    // Calculate new position
    const newSlideWidth = this.calculator.columnWidth + this.calculator.gap;
    let newPosition = 0;

    if (this.infiniteLoop) {
      const newSlidesToClone = this.getSlidesToClone();
      const newOriginalStartPosition = newSlidesToClone * newSlideWidth;
      newPosition = newOriginalStartPosition + (currentSlideIndex * newSlideWidth);
    } else {
      newPosition = currentSlideIndex * newSlideWidth;
      // Ensure we don't go out of bounds
      const maxScroll = this.calculator.snapPoints[this.calculator.snapPoints.length - 1] || 0;
      newPosition = Math.min(Math.max(newPosition, 0), maxScroll);
    }

    console.log('Resize Recalculation:', {
      currentSlideIndex,
      oldPosition: this.state.currentPosition,
      newSlideWidth,
      newPosition,
      infiniteLoop: this.infiniteLoop,
      slideFrom: this.state.currentPosition,
      slideTo: newPosition
    });

    // Jump to new position
    this.instantJumpTo(newPosition);

    this.lastWidth = currentWidth;
    this.isResizing = false;
    this.slider.dispatchEvent(new CustomEvent('sliderRecalculated'));
  }



  handleMouseDown(e) {
    if (this.state.isAnimating) return;
    if (this.state.animationId) {
      cancelAnimationFrame(this.state.animationId);
      this.state.animationId = null;
    }
    const rect = this.slider.getBoundingClientRect();
    this.state.isDown = true;
    this.state.isDragging = false;
    this.state.startX = e.clientX - rect.left;
    this.state.startY = e.clientY - rect.top;
    this.state.startPosition = this.state.currentPosition;
    this.state.lastX = e.clientX;
    this.state.velocity = 0;
  }

  handleMouseMove(e) {
    if (!this.state.isDown || this.state.isAnimating) return;

    const rect = this.slider.getBoundingClientRect();
    const currentX = e.clientX;
    const dx = currentX - (this.state.startX + rect.left);
    const dy = e.clientY - (this.state.startY + rect.top);

    if (!this.state.isDragging && Math.hypot(dx, dy) > SLIDER_CONFIG.DRAG_THRESHOLD) {
      this.state.isDragging = true;
      this.slider.classList.add('dragging');
    }

    if (!this.state.isDragging) return;
    e.preventDefault();

    this.state.velocity = (this.state.lastX - currentX) * SLIDER_CONFIG.VELOCITY_MULTIPLIER;
    this.state.lastX = currentX;
  }

  handleMouseUp() {
    if (!this.state.isDown || this.state.isAnimating) return;
    this.state.isDown = false;
    this.slider.classList.remove('dragging');

    if (this.state.isDragging) {
      this.state.wasDragging = true;

      // For fade animation, use swipe direction to change slides
      if (this.animationType === 'fade') {
        if (Math.abs(this.state.velocity) > SLIDER_CONFIG.VELOCITY_THRESHOLD) {
          const direction = this.state.velocity > 0 ? 'next' : 'prev';
          this.slideInDirection(direction);
        }
      } else if (this.infiniteLoop) {
        // For infinite loop, use slideInDirection which handles boundaries
        if (Math.abs(this.state.velocity) > SLIDER_CONFIG.VELOCITY_THRESHOLD) {
          const direction = this.state.velocity > 0 ? 'next' : 'prev';
          this.slideInDirection(direction);
        } else {
          // Snap to nearest slide in infinite loop
          this.snapToNearestInfinite();
        }
      } else {
        // Original finite slider logic
        if (Math.abs(this.state.velocity) > SLIDER_CONFIG.VELOCITY_THRESHOLD) {
          const direction = this.state.velocity > 0 ? 'next' : 'prev';
          this.slideInDirection(direction);
        } else {
          this.snapToNearest();
        }
      }
    }
    this.state.isDragging = false;
  }

  handleTouchStart(e) {
    if (this.state.isAnimating) return;
    if (this.state.animationId) {
      cancelAnimationFrame(this.state.animationId);
      this.state.animationId = null;
    }
    const touch = e.touches[0];
    const rect = this.slider.getBoundingClientRect();
    this.state.isDown = true;
    this.state.isDragging = false;
    this.state.scrollDirection = null;
    this.state.startX = touch.clientX - rect.left;
    this.state.startY = touch.clientY - rect.top;
    this.state.startPosition = this.state.currentPosition;
    this.state.lastX = touch.clientX;
    this.state.velocity = 0;
  }

  handleTouchMove(e) {
    if (!this.state.isDown || this.state.isAnimating) return;

    const touch = e.touches[0];
    const rect = this.slider.getBoundingClientRect();
    const currentX = touch.clientX;
    const currentY = touch.clientY;
    const dx = currentX - (this.state.startX + rect.left);
    const dy = currentY - (this.state.startY + rect.top);

    // Determine scroll direction on first significant movement
    if (!this.state.scrollDirection && Math.hypot(dx, dy) > SLIDER_CONFIG.DRAG_THRESHOLD) {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // If vertical movement is greater, it's a vertical scroll
      if (absDy > absDx) {
        this.state.scrollDirection = 'vertical';
      } else {
        this.state.scrollDirection = 'horizontal';
        this.state.isDragging = true;
        this.slider.classList.add('dragging');
      }
    }

    // If user is scrolling vertically, don't interfere
    if (this.state.scrollDirection === 'vertical') return;

    // Only prevent default and handle horizontal dragging
    if (this.state.isDragging) {
      e.preventDefault();
      this.state.velocity = (this.state.lastX - currentX) * SLIDER_CONFIG.VELOCITY_MULTIPLIER;
      this.state.lastX = currentX;
    }
  }

  handleTouchEnd() {
    if (!this.state.isDown || this.state.isAnimating) return;

    // If it was a vertical scroll, don't do anything
    if (this.state.scrollDirection === 'vertical') {
      this.state.isDown = false;
      this.state.scrollDirection = null;
      return;
    }

    this.state.isDown = false;
    this.slider.classList.remove('dragging');

    if (this.state.isDragging) {
      this.state.wasDragging = true;

      // For fade animation, use swipe direction to change slides
      if (this.animationType === 'fade') {
        if (Math.abs(this.state.velocity) > SLIDER_CONFIG.VELOCITY_THRESHOLD) {
          const direction = this.state.velocity > 0 ? 'next' : 'prev';
          this.slideInDirection(direction);
        }
      } else {
        // Determine direction based on accumulated velocity
        if (Math.abs(this.state.velocity) > SLIDER_CONFIG.VELOCITY_THRESHOLD) {
          const direction = this.state.velocity > 0 ? 'next' : 'prev';
          this.slideInDirection(direction);
        } else {
          // If no significant velocity, stay at current position
          this.snapToNearest();
        }
      }
    }
    this.state.isDragging = false;
    this.state.scrollDirection = null;
  }

  handleClick(e) {
    if (this.state.wasDragging) {
      e.preventDefault();
      e.stopPropagation();
      setTimeout(() => { this.state.wasDragging = false; }, 0);
    }
  }

  snapToNearestInfinite() {
    const slideWidth = this.calculator.columnWidth + this.calculator.gap;
    const slidesToClone = Math.max(this.calculator.slidesPerView - 1, this.calculator.slidesPerScroll);
    const originalStartPosition = slidesToClone * slideWidth;

    // Find nearest slide position
    const relativePosition = this.state.currentPosition - originalStartPosition;
    const nearestSlideIndex = Math.round(relativePosition / slideWidth);
    const target = originalStartPosition + (nearestSlideIndex * slideWidth);

    this.animateTo(target);
  }

  slideInDirection(direction) {
    // Prevent action if currently jumping or animating
    if (this.isJumping || this.state.isAnimating) return;

    if (this.infiniteLoop) {
      this.slideInfinite(direction);
    } else if (this.animationType === 'fade') {
      this.fadeToDirection(direction);
    } else {
      let target;
      if (direction === 'next') {
        target = this.calculator.getNextSnapPoint(this.state.currentPosition);
      } else {
        target = this.calculator.getPrevSnapPoint(this.state.currentPosition);
      }
      this.animateTo(target);
    }
  }

  slideInfinite(direction) {
    if (this.animationType === 'fade') {
      this.fadeInfinite(direction);
      return;
    }

    const slideWidth = this.calculator.columnWidth + this.calculator.gap;
    const slidesToClone = Math.max(this.calculator.slidesPerView - 1, this.calculator.slidesPerScroll);
    const slidesToScroll = this.calculator.slidesPerScroll;
    const scrollDistance = slidesToScroll * slideWidth;

    const originalStartPosition = slidesToClone * slideWidth;
    const originalEndPosition = originalStartPosition + (this.originalSlides.length * slideWidth);

    let target;

    if (direction === 'next') {
      target = this.state.currentPosition + scrollDistance;

      // If we're going to exceed the last real slide, jump to start first
      if (target >= originalEndPosition) {
        // Calculate how many slides past the end we'd be
        const overshoot = target - originalEndPosition;
        // Jump to start position plus the overshoot
        target = originalStartPosition + overshoot;
        const jumpFrom = originalStartPosition + overshoot - scrollDistance;
        this.instantJumpTo(jumpFrom, () => {
          this.animateTo(target);
        });
        return;
      }
    } else {
      target = this.state.currentPosition - scrollDistance;

      // If we're going to go before the first real slide, jump to end first
      if (target < originalStartPosition) {
        // Calculate how many slides before the start we'd be
        const undershoot = originalStartPosition - target;
        // Jump to end position minus the undershoot
        const jumpTarget = originalEndPosition - undershoot + scrollDistance;
        this.instantJumpTo(jumpTarget, () => {
          this.animateTo(jumpTarget - scrollDistance);
        });
        return;
      }
    }

    this.animateTo(target);
  }

  fadeInfinite(direction) {
    // For fade animation with infinite loop, we need to work with original slides only
    // and calculate their positions correctly
    const slideWidth = this.calculator.columnWidth + this.calculator.gap;
    const slidesToClone = Math.max(this.calculator.slidesPerView - 1, this.calculator.slidesPerScroll);
    const originalStartPosition = slidesToClone * slideWidth;

    // Find which original slide we're currently viewing
    const currentRelativePosition = this.state.currentPosition - originalStartPosition;
    const currentSlideIndex = Math.round(currentRelativePosition / slideWidth);

    let targetSlideIndex;
    if (direction === 'next') {
      // Move to next slide, wrap to first if at end
      targetSlideIndex = currentSlideIndex >= this.originalSlides.length - 1 ? 0 : currentSlideIndex + 1;
    } else {
      // Move to previous slide, wrap to last if at start
      targetSlideIndex = currentSlideIndex <= 0 ? this.originalSlides.length - 1 : currentSlideIndex - 1;
    }

    const target = originalStartPosition + (targetSlideIndex * slideWidth);
    this.fadeAnimateTo(target);
  }

  fadeToDirection(direction) {
    let target;
    if (this.infiniteLoop) {
      // For infinite loop, use the same logic as fadeInfinite
      this.fadeInfinite(direction);
      return;
    } else {
      // For non-infinite, use snap points
      if (direction === 'next') {
        target = this.calculator.getNextSnapPoint(this.state.currentPosition);
      } else {
        target = this.calculator.getPrevSnapPoint(this.state.currentPosition);
      }
    }

    if (target !== this.state.currentPosition) {
      this.fadeAnimateTo(target);
    }
  }

  fadeAnimateTo(target, duration = SLIDER_CONFIG.SCROLL_DURATION) {
    // Prevent starting new animation if currently jumping
    if (this.isJumping) return;

    if (this.state.animationId) cancelAnimationFrame(this.state.animationId);

    this.state.isAnimating = true;
    const fadeOutDuration = duration * 0.4; // 40% of time for fade out
    const fadeInDuration = duration * 0.4;  // 40% of time for fade in
    const moveDelay = duration * 0.2;       // 20% pause between

    // Fade out
    this.track.style.transition = `opacity ${fadeOutDuration}ms ease-out`;
    this.track.style.opacity = '0';

    setTimeout(() => {
      // Move to new position instantly while invisible
      this.state.currentPosition = target;
      this.track.style.transform = `translateX(${-target}px)`;

      this.slider.dispatchEvent(new CustomEvent('sliderMoved'));

      setTimeout(() => {
        // Fade back in
        this.track.style.transition = `opacity ${fadeInDuration}ms ease-in`;
        this.track.style.opacity = '1';

        setTimeout(() => {
          // Clean up
          this.track.style.transition = '';
          this.state.isAnimating = false;
        }, fadeInDuration);
      }, moveDelay);
    }, fadeOutDuration);
  }

  snapToNearest() {
    if (this.animationType === 'fade') {
      // For fade animation, we're already at a snap point
      return;
    }
    const target = this.calculator.findNearestSnapPoint(this.state.currentPosition);
    this.animateTo(target);
  }

  animateTo(target, duration = SLIDER_CONFIG.SCROLL_DURATION) {
    // Prevent starting new animation if currently jumping
    if (this.isJumping) return;

    if (this.state.animationId) cancelAnimationFrame(this.state.animationId);

    this.state.isAnimating = true;

    // Disable CSS transition for JS animation
    this.track.style.transition = 'none';

    const start = this.state.currentPosition;
    const distance = target - start;
    if (distance === 0) {
      this.state.isAnimating = false;
      this.track.style.transition = '';
      return;
    }

    const startTime = performance.now();

    const tick = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = duration === 0 ? 1 : Math.min(elapsed / duration, 1);
      this.state.currentPosition = start + distance * easeOutCubic(progress);
      this.track.style.transform = `translateX(${-this.state.currentPosition}px)`;
      this.slider.dispatchEvent(new CustomEvent('sliderMoved'));

      if (progress < 1) {
        this.state.animationId = requestAnimationFrame(tick);
      } else {
        this.state.animationId = null;
        this.state.isAnimating = false;

        // Restore CSS transition
        this.track.style.transition = '';

        // For infinite loop, check boundaries after animation completes
        if (this.infiniteLoop) {
          // Check boundary immediately after animation
          requestAnimationFrame(() => {
            this.checkInfiniteLoopBoundary();
          });
        }
      }
    };
    this.state.animationId = requestAnimationFrame(tick);
  }

  instantJumpTo(position, callback) {
    console.log('Instant Jump To:', position);
    this.isJumping = true;
    this.state.currentPosition = position;

    // Add data attribute for CSS targeting
    this.slider.setAttribute('data-jumping', 'true');

    // Completely disable transition
    this.track.style.transition = 'none';
    this.track.style.transform = `translateX(${-position}px)`;

    // Force a reflow to ensure the transition:none takes effect
    void this.track.offsetHeight;

    // Re-enable transitions after the jump
    requestAnimationFrame(() => {
      this.track.style.transition = '';
      this.slider.removeAttribute('data-jumping');
      requestAnimationFrame(() => {
        this.isJumping = false;
        // Execute callback after jump is complete
        if (callback) callback();
      });
    });
  }

  checkInfiniteLoopBoundary() {
    if (!this.infiniteLoop || this.isJumping || this.state.isAnimating) return;

    const slideWidth = this.calculator.columnWidth + this.calculator.gap;
    const slidesToClone = this.getSlidesToClone();

    const originalStartPosition = slidesToClone * slideWidth;
    const originalEndPosition = originalStartPosition + (this.originalSlides.length * slideWidth);

    const currentPos = this.state.currentPosition;

    // Add small tolerance for floating point comparison
    const tolerance = 1;

    // Check if we're at or past the end of the original slides (in the "after" clone zone)
    // This means we're on or past the last real slide
    if (currentPos >= originalEndPosition - tolerance) {
      const offsetFromEnd = currentPos - originalEndPosition;
      const newPosition = originalStartPosition + offsetFromEnd;

      console.log('Jumping from end clone to start:', { currentPos, newPosition, originalEndPosition, originalStartPosition });
      this.instantJumpTo(newPosition);
    }
    // Check if we're before the start of the original slides (in the "before" clone zone)
    // This means we're on the cloned slides at the beginning
    else if (currentPos < originalStartPosition - tolerance) {
      const offsetFromStart = currentPos - originalStartPosition;
      const newPosition = originalEndPosition + offsetFromStart;

      console.log('Jumping from start clone to end:', { currentPos, newPosition, originalStartPosition, originalEndPosition });
      this.instantJumpTo(newPosition);
    }
  }

  destroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();

    // Remove cloned slides if infinite loop was enabled
    if (this.infiniteLoop && this.track) {
      const clones = this.track.querySelectorAll('.slider-clone');
      clones.forEach(clone => clone.remove());
    }

    delete this.slider.sliderInstance;
  }
}

// ============================================================================
// SLIDER DOTS
// ============================================================================

export class SliderDots {
  constructor(slider) {
    this.slider = slider;
    this.instance = slider.sliderInstance;
    this.calculator = new SliderCalculator(slider);
    this.dotsContainer = slider.parentElement?.querySelector('.js-slider-dots');
    if (this.dotsContainer) this.init();
  }

  init() {
    this.update();
    this.slider.addEventListener('sliderMoved', () => this.update());
    this.slider.addEventListener('sliderRecalculated', () => this.handleRecalculation());
  }

  handleRecalculation() {
    this.calculator.update();
    this.update();
  }

  update() {
    if (!this.dotsContainer || !this.instance) return;

    const slideCount = this.instance.infiniteLoop
      ? this.instance.originalSlides.length
      : this.slider.querySelector('.slider-track')?.children.length || 0;
    const slidesToShow = this.calculator.slidesPerView;

    // Hide dots if not enough slides
    if (slideCount <= slidesToShow) {
      this.dotsContainer.style.display = 'none';
      return;
    }

    this.dotsContainer.style.display = '';

    this.dotsContainer.innerHTML = '';

    if (this.instance.infiniteLoop) {
      // For infinite loop, create dots based on original slides
      const slideWidth = this.calculator.columnWidth + this.calculator.gap;
      const slidesToClone = this.instance.getSlidesToClone();
      const originalStartPosition = slidesToClone * slideWidth;
      const totalOriginalWidth = this.instance.originalSlides.length * slideWidth;

      // Calculate the normalized current slide index
      // This handles cases where we are on a clone (before start or after end)
      let relativePos = this.instance.state.currentPosition - originalStartPosition;
      let currentIndex = Math.round(relativePos / slideWidth);

      // Normalize index to be within [0, length-1]
      const length = this.instance.originalSlides.length;
      const normalizedIndex = ((currentIndex % length) + length) % length;

      for (let i = 0; i < length; i++) {
        const dot = document.createElement('button');
        dot.className = 'slider-dot full-unstyled-button';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);

        if (i === normalizedIndex) {
          dot.classList.add('active');
        }

        dot.addEventListener('click', () => {
          const slidePosition = originalStartPosition + (i * slideWidth);
          if (this.instance.animationType === 'fade') {
            this.instance.fadeAnimateTo(slidePosition);
          } else {
            this.instance.animateTo(slidePosition);
          }
        });
        this.dotsContainer.appendChild(dot);
      }
    } else {
      // For finite slider, use snap points logic (existing code)
      const currentPosition = this.instance.state.currentPosition;
      const snapPoints = this.calculator.snapPoints;

      snapPoints.forEach((snap, index) => {
        const dot = document.createElement('button');
        dot.className = 'slider-dot full-unstyled-button';
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        const threshold = (this.calculator.columnWidth + this.calculator.gap) / 2;
        if (Math.abs(currentPosition - snap) < threshold) {
          dot.classList.add('active');
        }

        if (this.instance.animationType === 'fade') {
          dot.addEventListener('click', () => this.instance.fadeAnimateTo(snap));
        } else {
          dot.addEventListener('click', () => this.instance.animateTo(snap));
        }
        this.dotsContainer.appendChild(dot);
      });
    }
  }
}

// ============================================================================
// SLIDER ARROWS
// ============================================================================

export class SliderArrows {
  constructor(slider) {
    this.slider = slider;
    this.instance = slider.sliderInstance;
    this.calculator = new SliderCalculator(slider);
    this.prevButton = slider.parentElement?.querySelector('.js-slider-prev');
    this.nextButton = slider.parentElement?.querySelector('.js-slider-next');
    if (this.prevButton && this.nextButton) this.init();
  }

  init() {
    this.prevButton.addEventListener('click', () => this.scrollPrev());
    this.nextButton.addEventListener('click', () => this.scrollNext());
    this.updateButtonStates();
    this.slider.addEventListener('sliderMoved', () => this.updateButtonStates());
    this.slider.addEventListener('sliderRecalculated', () => this.handleRecalculation());
  }

  handleRecalculation() {
    this.calculator.update();
    this.updateButtonStates();
  }

  scrollPrev() {
    if (!this.instance) return;
    // Prevent rapid clicks during animation or jumping
    if (this.instance.state.isAnimating || this.instance.isJumping) return;
    this.instance.slideInDirection('prev');
  }

  scrollNext() {
    if (!this.instance) return;
    // Prevent rapid clicks during animation or jumping
    if (this.instance.state.isAnimating || this.instance.isJumping) return;
    this.instance.slideInDirection('next');
  }

  updateButtonStates() {
    if (!this.instance) return;
    const slideCount = this.slider.querySelector('.slider-track')?.children.length || 0;
    const slidesToShow = this.calculator.slidesPerView;

    // Hide arrows if not enough slides
    if (slideCount <= slidesToShow) {
      this.prevButton.style.display = 'none';
      this.nextButton.style.display = 'none';
      return;
    }

    this.prevButton.style.display = '';
    this.nextButton.style.display = '';

    // For infinite loop, never disable arrows
    if (this.instance.infiniteLoop) {
      this.prevButton.classList.remove('disabled');
      this.nextButton.classList.remove('disabled');
    } else {
      const maxScroll = this.calculator.snapPoints[this.calculator.snapPoints.length - 1] || 0;
      this.prevButton.classList.toggle('disabled', this.instance.state.currentPosition <= 0);
      this.nextButton.classList.toggle('disabled', this.instance.state.currentPosition >= maxScroll - 1);
    }
  }
}

// ============================================================================
// AUTO-SCROLL SLIDER
// ============================================================================

export class AutoScrollSlider {
  constructor(slider) {
    this.slider = slider;
    this.instance = slider.sliderInstance;
    this.calculator = new SliderCalculator(slider);

    // Store speeds
    this.desktopSpeed = parseFloat(slider.getAttribute('data-autoplay-speed')) || 0;
    this.mobileSpeed = parseFloat(slider.getAttribute('data-mobile-autoplay-speed'));

    // If mobile speed is not set, fallback to desktop speed
    if (isNaN(this.mobileSpeed)) {
      this.mobileSpeed = this.desktopSpeed;
    }

    console.log('AutoScrollSlider Init:', {
      id: this.slider.id,
      desktopSpeed: this.desktopSpeed,
      mobileSpeed: this.mobileSpeed,
      isMobile: isMobile(),
      innerWidth: window.innerWidth,
      breakpoint: SLIDER_CONFIG.MOBILE_BREAKPOINT
    });

    this.isManuallyPaused = false;
    this.isInViewport = true;
    this.timeoutId = null;
    this.observer = null;
    this.isPausedForResize = false;

    // Initialize current speed based on viewport
    this.updateSpeed();

    // Only init if we have a valid speed at start or might have one on resize
    if (this.currentSpeed > 0 || this.desktopSpeed > 0 || this.mobileSpeed > 0) {
      this.init();
    }
  }

  updateSpeed() {
    const newSpeed = isMobile() ? this.mobileSpeed : this.desktopSpeed;

    console.log('AutoScroll updateSpeed:', {
      id: this.slider.id,
      currentSpeed: this.currentSpeed,
      newSpeed,
      isMobile: isMobile(),
      isManuallyPaused: this.isManuallyPaused,
      isInViewport: this.isInViewport
    });

    if (this.currentSpeed !== newSpeed) {
      this.currentSpeed = newSpeed;
      this.intervalMs = this.currentSpeed * 1000;

      // If speed changed to 0, stop. If changed from 0, start.
      if (this.currentSpeed === 0) {
        this.pause();
      } else if (!this.isPaused && this.isInViewport) {
        this.startTimer();
      }
    }
  }

  init() {
    console.log('AutoScroll Init check:', {
      id: this.slider.id,
      currentSpeed: this.currentSpeed,
      desktopSpeed: this.desktopSpeed,
      mobileSpeed: this.mobileSpeed
    });
    if (this.currentSpeed > 0) {
      this.startTimer();
    }

    // Reset timer on any movement (manual or auto)
    this.slider.addEventListener('sliderMoved', () => this.resetTimer());

    this.slider.addEventListener('mousedown', () => this.pause());
    this.slider.addEventListener('mouseup', () => this.resume());
    this.slider.addEventListener('mouseenter', () => this.pause());
    this.slider.addEventListener('mouseleave', () => this.resume());

    // Add touch support for mobile
    this.slider.addEventListener('touchstart', () => this.pause(), { passive: true });
    this.slider.addEventListener('touchend', () => this.resume());

    this.slider.addEventListener('sliderResizeStart', () => {
      if (!this.isPausedForResize) {
        this.wasPausedBeforeResize = this.isPaused;
        this.isPausedForResize = true;
        this.pause();
      }
    });

    this.slider.addEventListener('sliderRecalculated', () => {
      this.calculator.update();
      this.updateSpeed(); // Check if speed needs to change based on new width

      if (this.isPausedForResize) {
        this.isPausedForResize = false;
        if (!this.wasPausedBeforeResize) {
          this.resume();
        }
      }
    });

    // Setup Intersection Observer to pause when out of viewport
    this.setupViewportObserver();
  }

  setupViewportObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          this.isInViewport = entry.isIntersecting;
          console.log('AutoScroll Intersection:', { id: this.slider.id, isIntersecting: entry.isIntersecting, isManuallyPaused: this.isManuallyPaused });

          if (entry.isIntersecting && !this.isManuallyPaused) {
            this.resume();
          } else {
            // We don't call pause() here because it sets isPaused/isManuallyPaused flags if we are not careful.
            // We just rely on startTimer checking isInViewport
            if (!entry.isIntersecting) {
              // Stop the timer but don't set manual pause
              if (this.timeoutId) clearTimeout(this.timeoutId);
            }
          }
        });
      },
      {
        root: null, // viewport
        threshold: 0.1 // Trigger when at least 10% visible
      }
    );

    this.observer.observe(this.slider);
  }

  startTimer() {
    if (this.timeoutId) clearTimeout(this.timeoutId);

    // Check both manual pause state AND viewport visibility
    if (this.isManuallyPaused || !this.isInViewport || this.currentSpeed === 0) {
      console.log('AutoScroll startTimer rejected:', {
        id: this.slider.id, isManuallyPaused: this.isManuallyPaused, isInViewport: this.isInViewport, currentSpeed: this.currentSpeed
      });
      return;
    }

    console.log('AutoScroll Timer Started', this.slider.id);
    this.timeoutId = setTimeout(() => {
      this.autoScroll();
    }, this.intervalMs);
  }

  resetTimer() {
    this.startTimer();
  }

  autoScroll() {
    if (!this.isManuallyPaused && this.isInViewport && this.instance && this.currentSpeed > 0) {
      console.log('AutoScroll Triggering Slide', this.slider.id);
      this.instance.slideInDirection('next');
    }
  }

  pause() {
    console.log('AutoScroll Paused Manually', this.slider.id);
    this.isManuallyPaused = true;
    if (this.timeoutId) clearTimeout(this.timeoutId);
  }

  resume() {
    console.log('AutoScroll Resumed Manually', this.slider.id);
    this.isManuallyPaused = false;
    this.startTimer();
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}

// ============================================================================
// ACTIVE SLIDES MANAGER
// ============================================================================

export class ActiveSlidesManager {
  constructor(slider) {
    this.slider = slider;
    this.track = slider.querySelector('.slider-track');
    this.observer = null;
    if (this.track) this.init();
  }

  init() {
    const slides = Array.from(this.track.children);
    if (!slides.length) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          entry.target.classList.toggle('active', entry.isIntersecting);
        });
      },
      {
        root: this.slider,
        threshold: 0.5 // Active when 50% visible
      }
    );
    slides.forEach(slide => this.observer.observe(slide));
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
  }
}

// ============================================================================
// INFINITE SLIDER
// ============================================================================

export class InfiniteSlider {
  constructor(slider) {
    this.slider = slider;
    this.track = slider.querySelector('.slider-track');
    if (this.track) this.init();
  }

  init() {
    this.updateAnimation();
    window.addEventListener('resize', () => this.updateAnimation());
    const sliderSection = this.slider.parentElement;
    if (sliderSection) {
      sliderSection.addEventListener('focusin', () => { /* Optionally pause */ });
      sliderSection.addEventListener('focusout', () => { /* Optionally resume */ });
    }
  }

  updateAnimation() {
    const trackWidth = this.track.scrollWidth / 2;
    const baseSpeed = parseFloat(this.slider.getAttribute('data-autoplay-speed')) || 30;
    const adjustedSpeed = Math.max(baseSpeed * (trackWidth / 1000), 15);
    this.track.style.animationDuration = `${adjustedSpeed}s`;
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

const initSliders = () => {
  console.log('Simple Slider: Initializing...');
  document.querySelectorAll('simple-slider').forEach(slider => {
    // DraggableSlider must be first as it provides the instance
    new DraggableSlider(slider);
    new SliderDots(slider);
    new SliderArrows(slider);
    new AutoScrollSlider(slider);
    new ActiveSlidesManager(slider);
  });

  document.querySelectorAll('.js-infinite-slider').forEach(slider => {
    new InfiniteSlider(slider);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSliders);
} else {
  initSliders();
}